// Stage 9 (eKYC 3.0) — adapters between BE-canonical PersonalDetailsResult /
// PUT body and the legacy redux shape consumed by `dadPersonalFunctional`.
//
// Keep this isolated so the page only does:
//   const { personalDetails, dropDownValues } = adaptPersonalGet(beData);
//   const beBody = adaptPersonalPut(legacyData);
//
// Phase D will retire this once the screen reads BE-canonical fields directly
// and BE returns dropdown master lists on /personal-details (BE-S9-PD1/L1).

// Generic boolean → '1'/'0' Key string used by the legacy KV shape for all
// yes/no flags (PEP, DIS booklet, BSDA, fnoSelected, stage10Required).
const boolToKey = (b) => (b ? '1' : '0');
const YN = (b) => (b ? 'Yes' : 'No');

// Display order for fields whose API response comes back in arbitrary sequence.
const DISPLAY_ORDER = {
    annualIncome:        ['LT1', '1T5', '5T10', '10T25', 'GT25'],
    investmentExperience: ['1', '2', '5', '10', '20', '25'],
};

// Converts a BE dropdown array {code, displayName}[] to {Key, Value, DisplayName}[].
// Key = raw API code — drives chip selection state (ChoiceChip matches every
// option against the current value with no dedup of its own) and is sent on
// PUT. If the BE ever emits the same code twice under different display names
// (seen from /lookups/{category}), every option sharing that code renders as
// simultaneously "selected" once the user picks it — dedupe here, first
// occurrence wins, so downstream consumers only ever see unique Keys.
const toFEFormat = (arr, fieldName) => {
    const order = DISPLAY_ORDER[fieldName];
    const seenKeys = new Set();
    const items = (arr || [])
        .map(({ code, displayName }) => ({
            Key: code,
            Value: displayName,
            DisplayName: displayName,
        }))
        .filter(({ Key }) => {
            if (seenKeys.has(Key)) return false;
            seenKeys.add(Key);
            return true;
        });
    if (!order) return items;
    return items.slice().sort((a, b) => {
        const ia = order.indexOf(a.Key);
        const ib = order.indexOf(b.Key);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });
};

// Module-level cache: populated by adaptPersonalGet when the API returns
// dropDownValues. adaptPersonalPut uses it for reverse-lookup so PUT codes
// stay in sync with whatever the API returned.
let _liveDropdowns = null;

// Hardcoded dropdown fallbacks (mirror the legacy FE labels). Used for fields
// not returned by the API (boolean flags, commodity, operation mode) and as a
// safety net when the API omits a field.
// `Key` is the BE enum CODE (sent on PUT, must match seeded `lookups.code` in
// PersonalDetailsService.ValidateLookupsAsync); `Value` is the legacy FE
// `actualValue` that page logic compares against. DisplayName is the label
// shown in ChoiceChip.
const DROPDOWNS = {
    MaritalStatus: [
        { Key: 'SINGLE', Value: 'Unmarried', DisplayName: 'Unmarried' },
        { Key: 'MARRIED', Value: 'Married', DisplayName: 'Married' },
        { Key: 'DIVORCED', Value: 'Divorced', DisplayName: 'Divorced' },
        { Key: 'WIDOWED', Value: 'Widowed', DisplayName: 'Widowed' },
    ],
    Qualification: [
        { Key: 'BELOW_10TH', Value: 'Below 10th', DisplayName: 'Below 10th' },
        { Key: 'HIGH_SCHOOL', Value: 'High School', DisplayName: 'High School (10th)' },
        { Key: 'HIGHER_SECONDARY', Value: 'Higher Secondary', DisplayName: 'Higher Secondary (12th)' },
        { Key: 'GRADUATE', Value: 'Graduate', DisplayName: 'Graduate' },
        { Key: 'POST_GRADUATE', Value: 'Post Graduate', DisplayName: 'Post Graduate' },
        { Key: 'DOCTORATE', Value: 'Doctorate', DisplayName: 'Doctorate / PhD' },
    ],
    Occupation: [
        { Key: 'PRIVATE_SECTOR', Value: 'Private Sector Service', DisplayName: 'Private Sector Service' },
        { Key: 'PUBLIC_SECTOR', Value: 'Public Sector', DisplayName: 'Public Sector / Govt Service' },
        { Key: 'BUSINESS', Value: 'Business', DisplayName: 'Business / Self Employed' },
        { Key: 'PROFESSIONAL', Value: 'Professional', DisplayName: 'Professional' },
        { Key: 'RETIRED', Value: 'Retired', DisplayName: 'Retired' },
        { Key: 'HOUSEWIFE', Value: 'Housewife', DisplayName: 'Housewife' },
        { Key: 'STUDENT', Value: 'Student', DisplayName: 'Student' },
        { Key: 'AGRICULTURE', Value: 'Agriculturist', DisplayName: 'Agriculture' },
    ],
    AnnualIncome: [
        { Key: 'BELOW_1L', Value: 'Below 1 Lac', DisplayName: 'Below 1 Lakh' },
        { Key: '1L_5L', Value: '1-5 Lac', DisplayName: '1-5 Lakhs' },
        { Key: '5L_10L', Value: '5-10 Lac', DisplayName: '5-10 Lakhs' },
        { Key: '10L_25L', Value: '10-25 Lac', DisplayName: '10-25 Lakhs' },
        { Key: '25L_1CR', Value: '25 Lac - 1 Cr', DisplayName: '25 Lakhs - 1 Crore' },
        { Key: 'ABOVE_1CR', Value: 'Above 1 Cr', DisplayName: 'Above 1 Crore' },
    ],
    InvestmentExp: [
        { Key: 'LESS_1_YEAR', Value: 'Less than 1 Yr', DisplayName: '< 1 Year' },
        { Key: '1_5_YEARS', Value: '1-5 Yrs', DisplayName: '1-5 Years' },
        { Key: '5_10_YEARS', Value: '5-10 Yrs', DisplayName: '5-10 Years' },
        { Key: 'MORE_10_YEARS', Value: '10+ Yrs', DisplayName: '> 10 Years' },
    ],
    CommodityCategory: [
        { Key: 'FARMER_PRODUCER', Value: 'Farmer producer organisation', DisplayName: 'Farmer producer organisation' },
        { Key: 'VALUE_CHAIN', Value: 'Value chain participants', DisplayName: 'Value chain participants' },
        { Key: 'OTHERS', Value: 'Others', DisplayName: 'Others' },
    ],
    PEP: [
        { Key: '0', Value: 'No', DisplayName: 'No' },
        { Key: '1', Value: 'Yes', DisplayName: 'Yes' },
    ],
    DISBOOKLET: [
        { Key: '0', Value: 'No', DisplayName: 'No' },
        { Key: '1', Value: 'Yes', DisplayName: 'Yes' },
    ],
    // DDPI opt-in (PersonalDetailsResult.ddpiEnabled, PR #16289/#16290,
    // CRM leads only — gated client-side by SHOWDDPI/showDdpi). Same
    // boolean-flag shape as DISBOOKLET/BSDA_SCHEME.
    DDPI: [
        { Key: '0', Value: 'No', DisplayName: 'No' },
        { Key: '1', Value: 'Yes', DisplayName: 'Yes' },
    ],
    BSDA_SCHEME: [
        { Key: '0', Value: 'No', DisplayName: 'No' },
        { Key: '1', Value: 'Yes', DisplayName: 'Yes' },
    ],
    SETTLE_CYCLE: [
        { Key: 'RUNNING_ACCOUNT', Value: 'Running Account', DisplayName: 'Running Account' },
        { Key: 'DAILY', Value: 'Daily', DisplayName: 'Daily' },
        { Key: 'MONTHLY', Value: 'Monthly', DisplayName: 'Monthly' },
        { Key: 'QUARTERLY', Value: 'Quarterly', DisplayName: 'Quarterly' },
    ],
    OPERATIONMODE: [
        { Key: 'SINGLE', Value: 'Single', DisplayName: 'Single' },
        { Key: 'JOINT', Value: 'Joint', DisplayName: 'Joint' },
    ],
    // dadPersonal line 826/2462: page compares `MTF.Key === 'YES'` and
    // sends `actualValue` ('Yes'/'No') back via adaptPersonalPut.
    MTFPRODUCT: [
        { Key: 'NO', Value: 'No', DisplayName: 'No' },
        { Key: 'YES', Value: 'Yes', DisplayName: 'Yes' },
    ],
    // Nominee Gender — Value/DisplayName MUST stay 'Male'/'Female' so the
    // NomineeDiyPopup gender→relation filter (which compares against 'Male'/
    // 'Female') keeps working. Fallback when the API omits the GENDER list.
    Gender: [
        { Key: 'MALE', Value: 'Male', DisplayName: 'Male' },
        { Key: 'FEMALE', Value: 'Female', DisplayName: 'Female' },
        { Key: 'TRANSGENDER', Value: 'Transgender', DisplayName: 'Transgender' },
    ],
};

// Stage 9 brokerage charges modal (FundTable). Seeds standard MOFSL
// Lifetime-AMC values so the "View Brokerage Charges" modal is never blank when
// the STANDARD branch of tariffData comes back empty.
export const DEFAULT_DPTARIFF = {
    DP_SCHEMENAME: 'TRX20',
    AMC: '₹0',
    AAC: '₹0',
    Trx_Charges_towards_shares_sold_Within_MOFSL: 'Rs. 35 or 0.035% (whichever is higher)',
    Off_market_Transfer_within_MOFSL: 'Rs. 40 or 0.040% (whichever is higher)',
    Off_market_Transfer_outside_MOFSL: 'Rs. 40 or 0.040% (whichever is higher)',
};

// Values of GET /personal-details -> tariffType. Branch on this, never on
// applicationType: a PP lead legitimately returns STANDARD when CBOS has no
// scheme for its partner, and the two can validly disagree.
export const TARIFF_TYPE = { STANDARD: 'STANDARD', PARTNER: 'PARTNER' };

// Row order and labels for the PARTNER (PP / franchisee) brokerage table, per
// the approved Partner-Channel /ba/scheme design. Every segment arrives from
// CBOS as a pair -- a raw code (eqDelivery) and a human-readable label
// (eqDeliveryValue); prefer the label and fall back to the code. `list` marks a
// comma-separated code list rather than a single value. Note the upstream
// spelling `commodityFeature*` -- that is what CBOS sends.
//
// The last five rows are the DP / demat charges CBOS added to the same row in
// Sep 2026 (AMC, AAC, TRXCHARGES_MOFSL, OFFMARKET_TRANSFER_IN/OUT_MOFSL). They
// are single display strings with no code / label pair, so they carry only a
// `value` key, and they reuse the standard table's labels. `amount` marks a
// value CBOS sends as a bare numeric ("399.0000") to be shown as a rupee amount.
export const PARTNER_TARIFF_ROWS = [
    { label: 'Delivery', value: 'eqDeliveryValue', code: 'eqDelivery' },
    { label: 'Intraday', value: 'eqTradingValue', code: 'eqTrading' },
    { label: 'Futures', value: 'derivativeFutureValue', code: 'derivativeFuture' },
    { label: 'Options', value: 'derivativeOptionValue', code: 'derivativeOption' },
    { label: 'Currency Future', value: 'currencyFutureValue', code: 'currencyFuture' },
    { label: 'Currency Option', value: 'currencyOptionValue', code: 'currencyOption' },
    { label: 'MF Buy', value: 'mfChargePurchaseValue', code: 'mfChargePurchase' },
    { label: 'MF Sell', value: 'mfChargeRedemptionValue', code: 'mfChargeRedemption' },
    { label: 'Commodity Delivery', value: 'commodityDeliveryValue', code: 'commodityDelivery' },
    { label: 'Commodity Future', value: 'commodityFeatureValue', code: 'commodityFeature' },
    { label: 'Commodity Options', value: 'commodityOptionValue', code: 'commodityOption' },
    { label: 'DP Tariff', value: 'dpTariffValue', code: 'dpTariff', list: true },
    { label: 'Accounts Maintenance Charges (AMC)', value: 'amc', amount: true },
    { label: 'Accounts Administration Charges (AAC)', value: 'aac', amount: true },
    { label: 'Trx Charges towards shares sold within MOFSL', value: 'trxChargesMofsl' },
    { label: 'Off market transfer within MOFSL', value: 'offmarketTransferInMofsl' },
    { label: 'Off market transfer outside MOFSL', value: 'offmarketTransferOutMofsl' },
];

// "399.0000" -> "₹399", "199.5000" -> "₹199.5". Anything that is not a bare
// numeric (e.g. "Rs. 30.00 OR 0.030% (whichever is higher)") is shown verbatim.
const formatPartnerAmount = (text) =>
    /^\d+(\.\d+)?$/.test(text) ? `₹${Number(text)}` : text;

// Trims a tariffData segment value and collapses "absent" to undefined, so the
// caller can omit the key rather than emit a blank row.
const tariffSegment = (value) => String(value == null ? '' : value).trim() || undefined;

// Maps one BE tariffData row to the legacy FundTable shape. Falls back to
// DEFAULT_DPTARIFF when the row is absent so the modal stays populated.
//
// The five brokerage segment rows (Delivery .. Commodity) are optional: the
// legacy scheme API never sent them, so an absent value leaves the key
// undefined and FundTable keeps rendering the DP-charges-only table it always
// did. Intraday and Futures share one slash-joined row, and Currency /
// Commodity take the future / delivery leg -- the same convention
// fundTransferSchemePopup uses for the scheme brokerage list.
const adaptStandardTariff = (t) => {
    if (!t) return DEFAULT_DPTARIFF;
    const intradayFutures = [tariffSegment(t.intraday), tariffSegment(t.futures)]
        .filter(Boolean)
        .join(' / ') || undefined;
    return {
        DP_SCHEMENAME: t.schemeName || DEFAULT_DPTARIFF.DP_SCHEMENAME,
        DELIVERY: tariffSegment(t.delivery),
        INTRADAY_FUTURES: intradayFutures,
        OPTIONS: tariffSegment(t.option),
        CURRENCY: tariffSegment(t.currencyFuture),
        COMMODITY: tariffSegment(t.commodityDelivery),
        AMC: t.amc || DEFAULT_DPTARIFF.AMC,
        AAC: t.aac || DEFAULT_DPTARIFF.AAC,
        Trx_Charges_towards_shares_sold_Within_MOFSL: t.trxChargesMofsl || DEFAULT_DPTARIFF.Trx_Charges_towards_shares_sold_Within_MOFSL,
        Off_market_Transfer_within_MOFSL: t.offmarketTransferInMofsl || DEFAULT_DPTARIFF.Off_market_Transfer_within_MOFSL,
        Off_market_Transfer_outside_MOFSL: t.offmarketTransferOutMofsl || DEFAULT_DPTARIFF.Off_market_Transfer_outside_MOFSL,
    };
};

// Maps one BE partnerTariffData row to [{ label, value }] display rows,
// skipping any segment whose label and code are both empty.
export const adaptPartnerTariff = (row) =>
    PARTNER_TARIFF_ROWS.reduce((rows, { label, value, code, list, amount }) => {
        const text = String((row && (row[value] || (code && row[code]))) || '').trim();
        if (!text) return rows;
        let display = text;
        if (list) display = text.split(',').map((s) => s.trim()).filter(Boolean).join(', ');
        else if (amount) display = formatPartnerAmount(text);
        rows.push({ label, value: display });
        return rows;
    }, []);

// GET /personal-details carries the brokerage data across three sibling fields:
// tariffData (the array production has always returned, null when empty),
// tariffType, and partnerTariffData (non-null only when tariffType is PARTNER).
// Normalise them into one envelope for the Brokerage Charges modal.
//
// `type` here is the EFFECTIVE render type, not a passthrough of the wire value:
// the partner table is chosen only when it actually has rows, so an unrecognised
// tariffType, a missing partnerTariffData or an all-null partner row all fall
// back to rendering tariffData exactly as before. The modal is never blank.
export const adaptTariff = (beData) => {
    const { tariffData, tariffType, partnerTariffData } = beData || {};
    // tariffData stays populated even on a PARTNER lead, so the standard rows
    // are always available as the fallback.
    const standard = adaptStandardTariff(Array.isArray(tariffData) ? tariffData[0] : null);
    const partnerRow = tariffType === TARIFF_TYPE.PARTNER && Array.isArray(partnerTariffData)
        ? partnerTariffData[0]
        : null;
    // Normalise "no usable partner rows" to null so the envelope is unambiguous:
    // a partner row of all-null segments is the same as having no partner data.
    const rows = partnerRow ? adaptPartnerTariff(partnerRow) : null;
    const partner = rows && rows.length ? rows : null;
    return {
        type: partner ? TARIFF_TYPE.PARTNER : TARIFF_TYPE.STANDARD,
        standard,
        partner,
    };
};

// GET /personal-details -> dpSchemeSelection: the CRM (branch) "DP Scheme" dropdown.
// Null for every non-CRM lead and while the BE rollout flag is off. Normalised to
// { show, options: [{ value, label, isDefault }], selected } for the "DP Scheme" row
// inside the Brokerage Charges modal (FundTable).
//
// `show` is a STRICT `=== true` check on the BE flag — the opposite polarity to
// SHOWFNO above, on purpose: this control is a CRM-only exception to the screen,
// so a BE that has not shipped the key, a null, or a stringy "true" must all
// keep it hidden. Missing means HIDE. Backend-authoritative; never derive it from
// applicationType client-side (same rule as SHOWDDPI).
export const adaptDpScheme = (beData) => {
    const sel = beData && beData.dpSchemeSelection;
    const options = Array.isArray(sel && sel.options)
        ? sel.options
            .filter((o) => o && o.code)
            .map((o) => ({
                value: String(o.code),
                label: o.displayName || String(o.code),
                isDefault: o.isDefault === true,
            }))
        : [];
    const defaultOption = options.find((o) => o.isDefault) || options[0];
    const selected = (sel && sel.selected) || (defaultOption && defaultOption.value) || '';
    return {
        show: !!sel && sel.show === true && options.length > 0,
        options,
        selected: String(selected),
    };
};

const findByKey = (arr, key) => arr.find((o) => String(o.Key) === String(key)) || null;

// Normalise a code to a canonical form so alternative representations of the
// same income/experience range match each other regardless of which naming
// convention the BE used:
//   stored-value style : "<1"  "1-5"  "5-10"  "10-25"  ">25"
//   lookup-code style  : "LT1" "1T5"  "5T10"  "10T25"  "GT25"
//   DROPDOWNS style    : "BELOW_1L" "1L_5L" "5L_10L" "10L_25L" "25L_1CR"/"ABOVE_1CR"
// All three collapse to the same token so findByKey fallback can cross-match.
const _normCode = (s) => String(s == null ? '' : s).toLowerCase().replace(/[_\s]+/g, '');
const RANGE_CODE_GROUPS = [
    ['<1',   'lt1',   'below1l'],
    ['1-5',  '1t5',   '1l5l',   '1lto5l'],
    ['5-10', '5t10',  '5l10l'],
    ['10-25','10t25', '10l25l'],
    ['>25',  'gt25',  '25l1cr', 'above1cr'],
];

const buildKV = (arr, key) => {
    if (key === undefined || key === null || key === '') return { Key: '', Value: '' };
    const hit = findByKey(arr, key);
    if (hit) return { Key: hit.Key, Value: hit.Value };
    // BE sometimes returns the displayName string instead of the code in the data field.
    const hitByLabel = arr.find(
        (o) => String(o.Value).toLowerCase() === String(key).toLowerCase() ||
               String(o.DisplayName).toLowerCase() === String(key).toLowerCase()
    );
    if (hitByLabel) return { Key: hitByLabel.Key, Value: hitByLabel.Value };
    // Range-notation fallback: match "<1"↔"LT1"↔"BELOW_1L", "1-5"↔"1T5"↔"1L_5L", etc.
    const normKey = _normCode(String(key));
    for (const group of RANGE_CODE_GROUPS) {
        if (group.includes(normKey)) {
            const hitByRange = arr.find((o) => group.includes(_normCode(String(o.Key))));
            if (hitByRange) return { Key: hitByRange.Key, Value: hitByRange.Value };
        }
    }
    return { Key: String(key), Value: String(key) };
};

/** Translate BE-canonical PersonalDetailsResult → legacy redux shape. */
export const adaptPersonalGet = (beData) => {
    const data = beData || {};

    // Merge API-supplied dropdowns over the hardcoded fallbacks for the six
    // enum fields. Boolean fields (PEP/DISBOOKLET/BSDA/MTF) and fields absent
    // from the API (OPERATIONMODE, CommodityCategory) keep the hardcoded values.
    const apiDD = data.dropDownValues || {};
    // Only override a hardcoded fallback when the API actually returned options.
    // NB: an empty array is truthy, so a `category: []` from the BE (e.g. a
    // not-yet-seeded personal_field_master category) would otherwise clobber the
    // hardcoded list with [] — guard on length. (This is why nominee Gender was
    // rendering empty: the BE returned gender: [].)
    const has = (arr) => Array.isArray(arr) && arr.length > 0;
    // Income / investment-experience can arrive under a few field names depending
    // on whether they came from GET /personal-details (inlined) or were hydrated
    // from GET /lookups/{category} (see personalApi.getMergedBeData). Accept the
    // common aliases so the BE-coded options always win over the hardcoded list.
    const apiIncome = apiDD.annualIncome || apiDD.annualIncomeRange || apiDD.income;
    const apiInvestment = apiDD.investmentExperience || apiDD.tradingExperience;
    const dropdowns = {
        ...DROPDOWNS,
        ...(has(apiDD.maritalStatus)        && { MaritalStatus:  toFEFormat(apiDD.maritalStatus,        'maritalStatus') }),
        ...(has(apiDD.education)            && { Qualification:  toFEFormat(apiDD.education,            'education') }),
        ...(has(apiDD.occupation)           && { Occupation:     toFEFormat(apiDD.occupation,           'occupation') }),
        ...(has(apiIncome)                  && { AnnualIncome:   toFEFormat(apiIncome,                  'annualIncome') }),
        ...(has(apiInvestment)              && { InvestmentExp:  toFEFormat(apiInvestment,              'investmentExperience') }),
        ...(has(apiDD.settlementPreference) && { SETTLE_CYCLE:   toFEFormat(apiDD.settlementPreference, 'settlementPreference') }),
        // Nominee dropdowns (EW: nominee Gender/Relation options). The BE serves
        // these on /personal-details dropDownValues (GENDER / RELATION lookup
        // categories); the nominee popup reads them off dropDownValues since the
        // legacy getNomineeRelationshipDropdown call isn't wired in eKYC 3.0.
        ...(has(apiDD.gender)   && { Gender:   toFEFormat(apiDD.gender,   'gender') }),
        ...(has(apiDD.relation) && { Relation: toFEFormat(apiDD.relation, 'relation') }),
    };
    _liveDropdowns = dropdowns;

    const verifierRejections = Array.isArray(data.verifierRejections) ? data.verifierRejections : [];
    const pepRejection = verifierRejections.find(
        (r) => r.rejectionCategory === 'PEP' && r.reviewStatus === 'REJECTED'
    );
    const incomeProofRejection = verifierRejections.find(
        (r) => r.rejectionCategory === 'Income Proof' && r.reviewStatus === 'REJECTED'
    );
    
    const personalDetails = {
        MaritalStatus: buildKV(dropdowns.MaritalStatus, data.maritalStatus),
        Qualification: buildKV(dropdowns.Qualification, data.education),
        Occupation: buildKV(dropdowns.Occupation, data.occupation),
        AnnualIncome: buildKV(dropdowns.AnnualIncome, data.annualIncomeRange),
        InvestmentExp: buildKV(dropdowns.InvestmentExp, data.tradingExperience),
        CommodityCategory: { Key: '', Value: '' }, // BE-S9-PD2: not yet on body
        PEP: { Key: boolToKey(data.pepDeclared), Value: YN(data.pepDeclared) },
        PEP_REJECT_REASON: pepRejection ? pepRejection.rejectionReason : '',
        INCOME_PROOF_REJECT_REASON: incomeProofRejection ? incomeProofRejection.rejectionReason : '',
        DISBOOKLET: { Key: boolToKey(data.disBooklet), Value: YN(data.disBooklet) },
        BSDA_SCHEME: { Key: boolToKey(data.bsdaEnabled), Value: YN(data.bsdaEnabled) },
        // DDPI (PR #16289/#16290): ddpiMarketPayin/ddpiPledgeRepledge/
        // ddpiTenderingOpenOffer always mirror ddpiEnabled server-side, so the
        // screen renders all three consent checkboxes off this single flag
        // instead of separate state. SHOWDDPI is the authoritative CRM-lead
        // gate (lead.ApplicationType == "CRM") — never derive it from
        // applicationType client-side.
        DDPI: { Key: boolToKey(data.ddpiEnabled), Value: YN(data.ddpiEnabled) },
        SHOWDDPI: boolToKey(data.showDdpi),
        // SHOWFNO gates the whole F&O Activation accordion. False only for a staff
        // (MOSL employee) lead — staff accounts are equity-only, and Stage 11 strips
        // derivatives for them regardless, so offering the segment just wastes an
        // income-proof step. Backend-authoritative; never derive it client-side.
        //
        // `!== false` rather than a bare truthy check, on purpose: boolToKey(undefined)
        // is '0', so against a BE that has not shipped showFno yet a bare check would
        // hide F&O from EVERY customer. Missing means show. Same reasoning as
        // INCOMEPROOFFETCHED's explicit `=== true` below.
        SHOWFNO: boolToKey(data.showFno !== false),
        // dadPersonal compares MTF.Key === 'YES' (line 826) and uses MTF
        // actualValue ('Yes'/'No') in adaptPersonalPut. Keep Keys YES/NO.
        MTF: { Key: data.mtfEnabled ? 'YES' : 'NO', Value: YN(data.mtfEnabled) },
        SETTLE_CYCLE: buildKV(dropdowns.SETTLE_CYCLE, data.settlementPreference),
        OPERATIONMODE: buildKV(dropdowns.OPERATIONMODE, data.operationMode),
        // Show-flags pending BE-S9-PD1 (TKT-0049). Default to render-everything.
        SHOWMTF: '1',
        MTFPRODUCTMESSAGE: '',
        MTFURL: '',
        SHOW_MAR_STATUS: '1',
        COM_CATE_SHOW_FLAG: '0',  // hidden until BE confirms (BE-S9-PD2)
        BSDA_SCHEME_SHOW: '1',
        ISNOMINEREQUIRED: '0',
        // Legacy father/spouse + nominee fields the screen optionally reads:
        FatherName: data.fatherName || '',
        MotherName: data.motherName || '',
        SpouseName: data.fatherSpouseName || '',
        // Drives the Father/Spouse chip on dadPersonal. `isSpouse` is the
        // authoritative flag when present; fall back to name-field heuristic
        // for older responses that don't carry the flag.
        Relation: (typeof data.isSpouse === 'boolean')
            ? (data.isSpouse ? 'Spouse' : 'Father')
            : (data.fatherName ? 'Father' : (data.fatherSpouseName ? 'Spouse' : '')),
        PDFREADCONSENT: '1',
        MITCCONSENT: '1',
        // Stage-10 gate: drives whether F&O/income-proof step renders.
        FNOSELECTED: boolToKey(data.fnoSelected),
        // Segment Selection API (BRD SEG v1.1, slice 1): F&O, Currency and
        // Commodity are now three independent flags. Equity is always included
        // and never a choice (equitySelected is informational, always true).
        // DERIVATIVESSELECTED is the BE-computed OR of the three — the income-
        // proof block binds to it. SEGMENTSELECTIONANSWERED is false until Other
        // Details has been submitted once ("not yet asked" vs "asked and declined").
        // A BE that has not shipped the split yet omits the new keys; boolToKey
        // (undefined) is '0', which is the correct "not selected" default.
        EQUITYSELECTED: boolToKey(data.equitySelected !== false),
        CURRENCYSELECTED: boolToKey(data.currencySelected),
        COMMODITYSELECTED: boolToKey(data.commoditySelected),
        DERIVATIVESSELECTED: boolToKey(
            typeof data.derivativesSelected === 'boolean'
                ? data.derivativesSelected
                : (data.fnoSelected || data.currencySelected || data.commoditySelected)
        ),
        SEGMENTSELECTIONANSWERED: boolToKey(data.segmentSelectionAnswered),
        STAGE10_REQUIRED: boolToKey(data.stage10Required),
        // EW-I6786: surface the backend F&O income-proof completion signal so the
        // F&O segment shows as completed on (re)load instead of resetting to "not
        // fetched". `incomeProofFetched` is true once the OneMoney/AA DATA webhook
        // has been processed — the same signal the BE uses to keep vs. down-promote
        // F&O — so dadPersonal can seed `personalValues.oneMoneyStatus` (which drives
        // the F&O "fetched" state / tick) from backend truth, not just from the
        // transient redux flag set on the OneMoney return. STAGE10_COMPLETE covers
        // both AA and manual-upload completion.
        //
        // `incomeProofSource` (personal_details.income_proof_source) is the
        // authoritative record of where the OneMoney/AA journey stands: NULL means
        // nothing meaningful happened yet (never started, or the user exited before
        // granting AA consent) — the F&O checkbox / "Fetch Bank statement digitally"
        // controls must be enabled. MANUAL_UPLOAD_PENDING means consent was granted
        // and the FI-data fetch is still processing; ACCOUNT_AGGREGATOR means it
        // finished — both must keep the controls locked so the user can't
        // re-trigger OneMoney mid-flight or after a completed fetch. OR'd with the
        // legacy incomeProofFetched boolean so environments where the BE hasn't
        // rolled out incomeProofSource yet keep working off the old signal.
        
        // ONEMONEYSTATUS: Used for LOCKING controls (prevent re-trigger during processing)
        ONEMONEYSTATUS: boolToKey(
            data.incomeProofSource === 'MANUAL_UPLOAD_PENDING' ||
            data.incomeProofSource === 'ACCOUNT_AGGREGATOR' ||
            data.incomeProofFetched
        ),
        // INCOMEPROOFFETCHED: Only reflects actual completion status (incomeProofFetched boolean).
        // NOT true during MANUAL_UPLOAD_PENDING (processing in progress).
        INCOMEPROOFFETCHED: boolToKey(data.incomeProofFetched === true),
        STAGE10_COMPLETE: boolToKey(data.stage10Complete),
    };
    return { personalDetails, dropDownValues: dropdowns };
};

// Normalise a label/code for tolerant cross-namespace matching (handles
// Lakh/Lac, Yr/Years, punctuation/spacing differences between the BE's two
// code sources).
const _norm = (s) => String(s == null ? '' : s).toLowerCase()
    .replace(/lakhs?|lacs?/g, 'lac')
    .replace(/crores?/g, 'cr')
    .replace(/years?|yrs?/g, 'yr')
    .replace(/[^a-z0-9]+/g, '');

/**
 * Map the selected option's display value → the BE code submitted on PUT.
 *
 * TKT-0037 (BE confirmed): `GET /personal-details` dropDownValues AND the PUT
 * validation are now BOTH the `personal_field_master` table — so the contract
 * is "submit the exact code the GET dropdown gave you." We therefore resolve
 * the selected label to the RAW live code (`_liveDropdowns` carries Key=code
 * straight from the GET), NOT a canonical Lookups remap. The hardcoded
 * `DROPDOWNS` code is a last-resort fallback only (fields the API didn't
 * return). This replaces the earlier TKT-0027 canonical-first mapping, which
 * 400'd ("Lookup validation failed") against personal_field_master and would
 * have stored the wrong code for the AOF/CBOS legacy platform.
 */
const labelToKey = (which, value) => {
    if (value === undefined || value === null || value === '') return null;
    const v = _norm(value);
    const findIn = (arr) => (arr || []).find(
        (o) => _norm(o.Value) === v || _norm(o.DisplayName) === v || _norm(o.Key) === v
    );
    // Raw GET code first (personal_field_master) per TKT-0037; canonical only as fallback.
    const live = findIn(_liveDropdowns && _liveDropdowns[which]);
    if (live) return live.Key;
    const canonical = findIn(DROPDOWNS[which]);
    if (canonical) return canonical.Key;
    return String(value).trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_');
};

// '1' / true / 'Yes' / 'Y' → true. Shared by the segment opt-in flags.
const flagOn = (v) => v === '1' || v === true || /^(yes|y)$/i.test(String(v || ''));

/** Adapt the legacy `data` object the page builds (lines ~1630-1645) into BE PUT body. */
export const adaptPersonalPut = (legacyData) => ({
    occupation: labelToKey('Occupation', legacyData.Occupation),
    annualIncomeRange: labelToKey('AnnualIncome', legacyData.AnnualIncome),
    tradingExperience: labelToKey('InvestmentExp', legacyData.InvestmentExp),
    settlementPreference: labelToKey('SETTLE_CYCLE', legacyData.SETTLE_CYCLE),
    maritalStatus: labelToKey('MaritalStatus', legacyData.MaritalStatus),
    education: labelToKey('Qualification', legacyData.Qualification),
    pepDeclared: legacyData.PEP === '1' || /^yes$/i.test(String(legacyData.PEP || '')),
    // `fnoSelected` is a first-class BE field (GET PersonalDetailsResult.fnoSelected
    // ↔ PUT body.fnoSelected). Carry the frontend F&O opt-in verbatim via the
    // explicit FNOSELECTED the page sends. Do NOT infer it from ISDERIVATIVE
    // (segment activation) or MTF (margin facility) — those are unrelated, and the
    // MTF overload made fnoSelected true even when F&O wasn't selected.
    fnoSelected: flagOn(legacyData.FNOSELECTED),
    // Segment Selection API (BRD SEG v1.1, slice 1). Sending either split key
    // opts this client into the three-way model, so ALWAYS send both — a missing
    // key would otherwise be read as false, and omitting both would make the BE
    // expand fnoSelected to the legacy bundle (fno = currency = commodity).
    // equitySelected is mandatory-true: the BE 400s on false, so never derive
    // it from the (always-disabled) Equity checkbox.
    equitySelected: true,
    currencySelected: flagOn(legacyData.CURRENCYSELECTED),
    commoditySelected: flagOn(legacyData.COMMODITYSELECTED),
    disBooklet: legacyData.DISBOOKLET === '1' || /^yes$/i.test(String(legacyData.DISBOOKLET || '')),
    bsdaEnabled: legacyData.BSDA_SCHEME === '1' || /^yes$/i.test(String(legacyData.BSDA_SCHEME || '')),
    // legacyData.MTF here is the actualValue from mtfArray ('Yes'/'No').
    mtfEnabled: legacyData.MTF === '1' || /^yes$/i.test(String(legacyData.MTF || '')),
    // legacyData.DDPI is the actualValue from ddpiArray ('Yes'/'No'), CRM-only
    // (SHOWDDPI-gated on the read side). BE derives the three consent
    // checkboxes from this single flag — no separate PUT fields needed.
    ddpiEnabled: legacyData.DDPI === '1' || /^yes$/i.test(String(legacyData.DDPI || '')),
    operationMode: labelToKey('OPERATIONMODE', legacyData.OPERATIONMODE),
    // Legacy page builds FatherName (PascalCase) + Relation ('Father'/'Spouse').
    // Route to the correct BE field based on Relation.
    fatherName: legacyData.Relation === 'Spouse'
        ? undefined
        : (legacyData.fatherName || legacyData.FatherName || undefined),
    fatherSpouseName: legacyData.Relation === 'Spouse'
        ? (legacyData.fatherName || legacyData.FatherName || undefined)
        : (legacyData.fatherSpouseName || undefined),
    isSpouse: legacyData.Relation === 'Spouse',
    motherName: legacyData.motherName || legacyData.MotherName || undefined,
    // commodityCategory, tariffPlan, custodianCode, nriAccountType
    // pending BE confirmation (TKT-0049 BE-S9-PD2/3).
    platform: 'WEB',
});
